import { OnDestroy, Type } from '@angular/core';
import {
    EMPTY,
    from,
    MonoTypeOperatorFunction,
    Observable,
    ObservableInput,
    OperatorFunction,
    pipe,
    Subject,
    UnaryFunction
} from 'rxjs';
import {
    exhaustMap,
    filter,
    finalize,
    first,
    map,
    mergeMap,
    startWith,
    takeUntil,
    tap
} from 'rxjs/operators';

interface LifecycleAwareComponent extends OnDestroy, Function {
    lsq__onDestroy$: Subject<true>;
}

export function LifecycleAware(): ClassDecorator {
    return (_target: any) => {
        const target = _target as LifecycleAwareComponent;
        if (!target.prototype.hasOwnProperty('lsq__onDestroy$')) {
            const originalOnDestroy =
                typeof target.prototype.ngOnDestroy === 'function'
                    ? target.prototype.ngOnDestroy
                    : null;

            target.prototype.lsq__onDestroy$ = new Subject<true>();
            target.prototype.ngOnDestroy = function () {
                target.prototype.lsq__onDestroy$.next(true);
                if (originalOnDestroy) {
                    originalOnDestroy.call(this);
                }
            };
        }
    };
}

export const forLifeOf = <ValueType>(instance: any): MonoTypeOperatorFunction<ValueType> => {
    // Check to make sure the class has the @LifecycleAware decorator.
    const isLifecycleAware = !!(instance as LifecycleAwareComponent).lsq__onDestroy$;

    if (!isLifecycleAware) {
        throw new Error(
            `Tried to use forLifeOf inside ${instance.constructor.name}, which is not a @LifecycleAware component.`
        );
    }
    return takeUntil<ValueType>((instance as LifecycleAwareComponent).lsq__onDestroy$);
};

// Taken from https://github.com/ReactiveX/rxjs/issues/1777#issuecomment-466267803
export function exhaustMapAndSendLast<T, R>(
    project: (value: T, index: number) => ObservableInput<R>
): OperatorFunction<T, R> {
    return (source) => {
        const subject = new Subject<[T, number] | undefined>();
        let queued: [T, number] | undefined;
        let request = subject.pipe(exhaustMap((value) => from(project(...value))));
        let isFirst = true;
        let inProgress = false;
        return source.pipe(
            mergeMap((value, index) => {
                if (inProgress) {
                    queued = [value, index];
                } else {
                    subject.next([value, index]);
                }

                if (isFirst) {
                    request = subject.pipe(
                        startWith([value, index] as [T, number]),
                        tap(() => (inProgress = true)),
                        exhaustMap((innerValue) =>
                            from(project(...innerValue)).pipe(
                                finalize(() => {
                                    inProgress = false;
                                    if (queued) {
                                        const queuedCopy = queued;
                                        queued = undefined;
                                        subject.next(queuedCopy);
                                    }
                                })
                            )
                        )
                    );
                    isFirst = false;
                    return request;
                }
                return EMPTY;
            })
        );
    };
}

export function instanceOf<ActionType = any>(actionType: Type<ActionType>) {
    return pipe(filter<ActionType>((action) => action instanceof actionType));
}

export function firstInstanceOf<ActionType = any>(actionType: Type<ActionType>) {
    return pipe(instanceOf(actionType), first());
}

export function ignoreNullish<ValueType>(): UnaryFunction<
    Observable<ValueType | undefined | null>,
    Observable<ValueType>
> {
    return pipe(filter((value) => value != null));
}

export function mapToKey<ValueType extends { [key: string]: any }, KeyType extends keyof ValueType>(
    key: KeyType
): UnaryFunction<Observable<ValueType>, Observable<ValueType[typeof key]>> {
    return pipe(map((value) => value[key]));
}
